import { useEditor, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { Action, Actions } from "src/types";
import styled, { css } from "styled-components";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import RegistrationWidget from "@/components/registration-widget/registration-widget-component";
import SimpleFlowRedirect from "@/components/simple-flow-redirect/simple-flow-redirect-component";
import useAnalytics from "@/hooks/use-analytics";
import { isAttendeesPage } from "@/utils/is-attendees-page";
import { observeScrollToUnlock } from "@/utils/toggle-page-scroll-lock";

import ButtonSettings from "./button-settings";

interface ButtonProps {
  isLink?: boolean;
  text: string;
  action: Action;
  colorSet?: string[];
  noCustomization?: boolean;
}

export default function Button(props: ButtonProps) {
  const [isTicketSelectorOpen, setTicketSelectorOpen] = React.useState(false);
  const { logAnalytics } = useAnalytics();

  const { isEditing } = useEditor((state) => ({
    isEditing: state.options.enabled,
  }));

  const {
    connectors: { connect },
  } = useNode();

  const { action, isLink, text, ...rest } = props;

  const handleClick = React.useCallback(
    async (event?: React.MouseEvent<HTMLElement>) => {
      if (!action) {
        return;
      }

      switch (action.kind) {
        case Actions.TicketSelector: {
          setTicketSelectorOpen(true);
          if (isAttendeesPage()) {
            logAnalytics("REGISTER_TICKET_CLICKED", {
              type: "canvas",
            });
          }
          break;
        }
        case Actions.SectionOfPage: {
          event.preventDefault();
          await observeScrollToUnlock();
          const node = document.getElementById(action.nodeId);
          if (node) {
            scrollIntoView(node, {
              block: "start",
              behavior: "smooth",
            });
          }
          break;
        }
        case Actions.ExternalLink: {
          if (isLink) {
            return;
          }
          window.open(action.href, "_blank");
          break;
        }
        case Actions.TopOfPage: {
          await observeScrollToUnlock();
          window.scroll({
            top: 0,
            left: 0,
            behavior: "smooth",
          });
          break;
        }
        case Actions.BottomOfPage: {
          await observeScrollToUnlock();
          window.scroll({
            top:
              document.body.scrollHeight ||
              document.documentElement.scrollHeight,
            left: 0,
            behavior: "smooth",
          });
          break;
        }
      }
    },
    [action, isLink, logAnalytics]
  );

  const handlers = React.useMemo(() => {
    const handlers: Record<string, any> = {
      onClick: (event) => {
        if (isEditing) {
          event.preventDefault();
          return;
        }
        handleClick(event);
      },
    };

    if (isLink && props.action.kind === Actions.ExternalLink) {
      handlers.href = props.action.href;
      handlers.target = "_blank";
    }

    return handlers;
  }, [handleClick, isEditing, isLink, props.action]);

  const sectionHref = React.useMemo(() => {
    return isLink && action.kind === Actions.SectionOfPage
      ? { href: `#${action.nodeId}` }
      : {};
  }, [isLink, action]);

  const ButtonComponent =
    props.noCustomization === undefined ? CustomStyledButton : StyledButton;

  return (
    <>
      {action?.kind === Actions.TicketSelector && (
        <SimpleFlowRedirect
          isOpen={isTicketSelectorOpen}
          onClose={() => setTicketSelectorOpen(false)}
          renderWidget={(isShowing, handleClose) => (
            <RegistrationWidget isOpen={isShowing} onClose={handleClose} />
          )}
        />
      )}
      <ButtonComponent
        as={isLink && "a"}
        ref={connect}
        {...handlers}
        {...rest}
        {...sectionHref}
        className="button-primitive"
      >
        {text}
      </ButtonComponent>
    </>
  );
}

const keyboardFocus = css`
  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
    box-shadow: 0 0 0 4px #000;
  }
`;

const StyledButton = styled.button`
  font-size: ${rem(16)};
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
  color: ${({ theme }) => useInvertedColor(theme.colors[ColorLabels.PRIMARY])};
  border: none;
  text-decoration: none;
  ${keyboardFocus}
`;

const CustomStyledButton = styled.button<ButtonProps>`
  font-size: ${rem(16)};
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
  cursor: pointer;
  background-color: ${({ colorSet, theme }) =>
    colorSet ? colorSet[0] + " !important" : theme.colors[ColorLabels.PRIMARY]};
  color: ${({ colorSet, theme }) =>
    colorSet
      ? colorSet[1] + " !important"
      : useInvertedColor(theme.colors[ColorLabels.PRIMARY])};

  text-decoration: none;
  ${({ colorSet = [undefined, undefined, "transparent"] }) =>
    colorSet[2] !== "transparent"
      ? "border:  0.125rem solid " + colorSet[2] + "!important"
      : "border: none !important;"};
  ${keyboardFocus}
`;

Button.craft = {
  related: {
    settings: ButtonSettings,
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
};
